import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tileLayer, latLng, marker, icon, Icon } from 'leaflet';
import { Lightbox } from 'ngx-lightbox';
import { NgForm, FormGroup, FormBuilder, Validators, FormArray, FormControl } from '@angular/forms';
import * as FormValidation from 'src/app/shared/_helpers/get-form-validation-errors';
import { environment } from 'src/environments/environment';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { SharedService } from 'src/app/shared/shared.service';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { VssOrganizationsService } from '../vss-organizations.service';
import { VssOrganization } from 'src/app/shared/models/vss-organization.model';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-vss-update-organization',
    templateUrl: './vss-update-organization.component.html',
    styleUrls: ['./vss-update-organization.component.css'],
    standalone: false
})
export class VssUpdateOrganizationComponent implements OnInit, OnDestroy {

  @ViewChild('orgInfoForm') form: NgForm;
  @ViewChild('newMapLatitude') newMapLatitude: ElementRef;
  @ViewChild('newMapLongitude') newMapLongitude: ElementRef;

  private defaultIcon: Icon = icon({
    iconUrl: 'assets/leaflet/marker-icon.png',
    shadowUrl: 'assets/leaflet/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  public options = {
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '...' })
    ],
    zoom: 8,
    center: latLng(46.6553557, 10.8692822)
  };
  public layers = [];
  public currentOrgLanguage = 'de';
  public formGroup: FormGroup;
  public languageToString = ({
    'de': 'German - DE-de',
    'it': 'Italian - IT-it',
    'en': 'Ladin - lld',
  });
  public icons: { [name: string]: any } = {
    'faLink': faLink
  };
  public imagePath: any;
  public validationError: FormValidation.AllValidationErrors;
  public organizationType: number;
  public fileSizeError = false;
  public sportCategories: any[] = [];
  public sportCategoriesFormArray = new FormArray([]);

  private _organization: VssOrganization;
  private _newLocation = ({ 'latitude': undefined, 'longitude': undefined, });
  private _imageToUpload: File = null;

  constructor(private _activatedRoute: ActivatedRoute, private _router: Router, private _vssOrganizationsService: VssOrganizationsService,
    private _formBuilder: FormBuilder, private _lightbox: Lightbox, private _toastService: ToastService,
    private _sharedService: SharedService) {
    this.formGroup = this._formBuilder.group({
      id: [''],
      adminName: [''],
      name_it: [{ value: '', disabled: true }],
      name_de: [{ value: '', disabled: true }],
      name_en: [{ value: '', disabled: true }],
      imagePath: [''],
      address_de: [''],
      address_it: [''],
      address_en: [''],
      description_de: [''],
      description_it: [''],
      description_en: [''],
      latitude: [''],
      longitude: [''],
      phone: [''],
      webPage: [''],
      webPage_en: [''],
      webPage_de: [''],
      email: [''],
    });
  }

  ngOnInit(): void {
    const urlPramas = this._activatedRoute.snapshot.params;
    if (!urlPramas.orgId) {
      this._router.navigate(['/vss']);
    }
    combineLatest([
      this._vssOrganizationsService.getSportCategories(),
      this._vssOrganizationsService.getVssOrganization(urlPramas.orgId)
    ]).subscribe(result => {
      this.sportCategories = this._sharedService.splitArrayIntoChunksOfLen(result[0], 10);
      this._organization = result[1];
      this.formGroup.patchValue(this._organization);
      if (this._organization.latitude && this._organization.longitude) {
        this._addMarker(this._organization.latitude, this._organization.longitude);
      }
      if (this._organization.imagePath) {
        this.imagePath = environment.vssImageUrl + this._organization.imagePath;
      }
      // tslint:disable-next-line:radix
      this.organizationType = parseInt(localStorage.getItem('orgId'));
      const sportCategory = [];
      if (this._organization.sportCategories.length > 0) {
        this._organization.sportCategories.map(id => {
          sportCategory.push(this._sharedService.deepSearchSportCategoriesById(id, result[0]));
        });
      }
      this._sharedService.organizationNameSubject.next({
        'de': this._organization.name_de + (sportCategory.length > 0 ? ' -' + sportCategory.map(s => ' ' + s.name_de) : ''),
        'it': this._organization.name_it + (sportCategory.length > 0 ? ' -' + sportCategory.map(s => ' ' + s.name_it) : '')
      });
      const formArray = this.sportCategoriesFormArray as FormArray;
      this._organization.sportCategories.map(sportCategoryId => {
        formArray.push(new FormControl(sportCategoryId));
      });
    });
  }

  ngOnDestroy(): void {
    this._toastService.remove();
  }

  onLanguageChange(language: string) {
    this.currentOrgLanguage = language;
  }

  submitForm() {
    if (this.form.valid) {
      if (this._newLocation.latitude && this._newLocation.longitude) {
        this.formGroup.patchValue({ 'latitude': this._newLocation.latitude });
        this.formGroup.patchValue({ 'longitude': this._newLocation.longitude });
      }
      const object = this.form.value as VssOrganization;
      object.sportCategories = this.sportCategoriesFormArray.value;
      this._vssOrganizationsService.updateVssOrganization(this.form.value).subscribe(result => {
        if (this._imageToUpload) {
          const imageFileNameArray = this._imageToUpload.name.split('.');
          const imageNameFinale = imageFileNameArray[imageFileNameArray.length - 1].toLocaleLowerCase();
          const imageName = Math.random().toString(36).slice(-8) + '.' + imageNameFinale;
          this._vssOrganizationsService.submitOrganizationImage(this._imageToUpload, imageName, this._organization.id)
            .subscribe(requestResult => {
              console.log(requestResult);
            }, responseError => {
              console.error('Image Upload :: ', responseError);
            });
        }
        this._toastService.show('', {
          classname: 'bg-success text-light',
          delay: 2000,
          autohide: true,
        });
      }, error => {
        console.error('Organization Upload :: ', error);
      });
    } else {
      FormValidation.getFormValidationErrors(this.formGroup.controls).map(error => {
        console.log(error);
        this.formGroup.get(error.control_name).setErrors({ required: true });
      });
    }
  }

  onSportCategoryChange(event) {
    const formArray: FormArray = this.sportCategoriesFormArray as FormArray;
    const elementValue = parseInt(event.target.value, 10);
    if (event.target.checked) {
      formArray.push(new FormControl(elementValue));
    } else {
      formArray.controls.filter((v, i) => {
        if (v.value === elementValue) {
          formArray.removeAt(i);
          return;
        }
      });
    }
    console.log(this.sportCategoriesFormArray.value);
  }

  onMapResetClick() {
    this.newMapLatitude.nativeElement.value = '';
    this.newMapLongitude.nativeElement.value = '';
    this._addMarker(this._organization.latitude, this._organization.longitude);
  }

  onMapClick(event: any) {
    this._addMarker(event.latlng.lat.toString(), event.latlng.lng.toString());
  }


  handleImageFileInput(event: any) {
    const files = event.target.files;
    this.fileSizeError = false;
    if (files.length === 0) { return; }
    if (files.length > 1) { return; }
    const mimeType = files[0].type;
    if (mimeType.match(/image\/*/) == null) {
      // return this._toastr.warning(this._translate.instant('event.advertiseEvent.imageError'));
      return;
    }
    if (files[0].size > 9500000) {
      this.fileSizeError = true;
      return;
    }
    const reader = new FileReader();
    this._imageToUpload = files.item(0);
    reader.readAsDataURL(files[0]);
    reader.onload = (_event) => {
      this.imagePath = reader.result;
    };
  }

  openLightbox() {
    this._lightbox.open([{
      src: this.imagePath,
      caption: '',
      thumb: this.imagePath
    }], 0);
  }

  _addMarker(lat: string, lng: string) {
    this._newLocation.latitude = lat;
    this._newLocation.longitude = lng;
    this.layers = [];
    this.layers.push(marker([parseFloat(lat), parseFloat(lng)], { icon: this.defaultIcon }));
    this.options.center = latLng(parseFloat(lat), parseFloat(lng));
    if (this._newLocation.latitude && this._newLocation.longitude) {
      this.formGroup.patchValue({ 'latitude': this._newLocation.latitude });
      this.formGroup.patchValue({ 'longitude': this._newLocation.longitude });
    }
  }

  deleteImage() {
    this._vssOrganizationsService.deleteImage(this._organization.id.toString()).subscribe(result => {
      this.imagePath = undefined;
    });
  }

  setNewMapLocation() {
    const newMapLatitude = this.newMapLatitude.nativeElement.value;
    const newMapLongitude = this.newMapLongitude.nativeElement.value;
    if (newMapLatitude && newMapLongitude) {
      this._addMarker(newMapLatitude, newMapLongitude);
    }
  }
}
